import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from './hooks/useAuth'
import { useTranslation } from './hooks/useTranslation'
import { useLocation } from 'react-router-dom'
import { ContainerScroll } from './components/ContainerScroll'
import { FeaturesSectionWithHoverEffects } from './components/FeaturesSectionWithHoverEffects'
import SectionWithMockup from './components/blocks/SectionWithMockup'
import { Footer } from './components/Footer'
import { GlobalCookieConsent } from './components/GlobalCookieConsent'
import { ReferralManager } from './components/ReferralManager'
import { YouTubeVideo } from './components/YouTubeVideo'
import { ProductHuntWidget } from './components/ProductHuntWidget'
import { ArrowRight, Brain } from 'lucide-react'
import AutoScroll from "embla-carousel-auto-scroll"
import { Carousel, CarouselContent, CarouselItem } from "./components/ui/carousel"
import { SparklesPreview } from "./components/ui/SparklesPreview";
import ReactGA from 'react-ga4';

// SEO Optimization Hook
const useSEO = (title, description) => {
  useEffect(() => {
    document.title = title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Schema.org structured data
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Hiperyon",
      "description": description,
      "url": "https://hiperyon.com",
      "applicationCategory": "ProductivityApplication",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "ratingCount": "6"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR"
      }
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, [title, description]);
};
export default function App() {
  const { isAuthenticated, loading } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [imgError, setImgError] = useState(false)
  const [newsletterMsg, setNewsletterMsg] = useState("");

  // Initialiser Google Analytics une seule fois
  useEffect(() => {
    ReactGA.initialize('G-RKHHB1N1X7');
  }, []);

  // SEO Optimization
  useSEO(
    "Hiperyon - Multipliez votre productivité avec plusieurs IA en même temps",
    "Utilisez ChatGPT, Gemini, Mistral et Grok simultanément. Gagnez 3 à 4x plus de temps et augmentez votre efficacité de 200%. Extension gratuite pour optimiser votre travail avec l'IA."
  );

  const location = useLocation();
  // Suppression de la redirection automatique vers le dashboard

  // Afficher un loader pendant la vérification
  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
        <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mx-auto mb-4"></div>
            <p className="text-slate-400">{t('loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
      {/* Referral Manager - détecte et stocke les codes referral */}
      <ReferralManager />
      
      {/* Header/Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 flex items-center justify-center">
                <img src="/logo_svg.svg" alt="Hiperyon" className="h-10 w-10" />
              </div>
              <span className="font-bold text-xl text-white">Hiperyon</span>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated() ? (
                <Link
                  to="/dashboard"
                  className="px-6 py-2 rounded-lg bg-emerald-500 text-slate-950 font-medium hover:bg-emerald-400 transition"
                >
                  Go to dashboard
                </Link>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition"
                  >
                    {t('login')}
                  </Link>
                  <Link 
                    to="/signup" 
                    className="px-6 py-2 rounded-lg bg-emerald-500 text-slate-950 font-medium hover:bg-emerald-400 transition"
                  >
                    {t('signup')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

  {/* Hero Section with Scroll Animation */}
  <div className="pt-0 mt-0" style={{marginTop: '-116px', paddingTop: 0}}>
        <ContainerScroll
          titleComponent={
            <div className="space-y-6 relative w-full max-w-4xl mx-auto">
              {/* AI Logos scattered around with 3D depth effect */}

              <div className="absolute inset-0 pointer-events-none" style={{perspective: '1200px', width: '100%', height: '100%'}}>
                {/* MOBILE: logos centrés et serrés */}
                <div className="flex justify-center gap-2 absolute left-1/2 -translate-x-1/2 -top-20 z-10 w-max sm:hidden">
                  {/* Grok */}
                  <div className="animate-float" style={{animationDelay: '0s'}}>
                    <div className="w-16 h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/grok.png" alt="Grok" className="w-10 h-10 object-contain" />
                    </div>
                  </div>
                  {/* ChatGPT */}
                  <div className="animate-float" style={{animationDelay: '0.1s'}}>
                    <div className="w-16 h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center pixelate-effect" style={{filter: 'drop-shadow(0 2px 8px rgba(16, 185, 129, 0.15))'}}>
                      <img src="/gpt.png" alt="ChatGPT" className="w-10 h-10 object-contain pixelate-img" />
                      <div className="pixel-overlay" />
                    </div>
                  </div>
                  {/* Claude */}
                  <div className="animate-float" style={{animationDelay: '0.2s'}}>
                    <div className="w-16 h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/claude.png" alt="Claude" className="w-10 h-10 object-contain" />
                    </div>
                  </div>
                  {/* Gemini */}
                  <div className="animate-float" style={{animationDelay: '0.3s'}}>
                    <div className="w-16 h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center pixelate-effect" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/gemini.png" alt="Gemini" className="w-10 h-10 object-contain pixelate-img" />
                      <div className="pixel-overlay" />
                    </div>
                  </div>
                  {/* Mistral */}
                  <div className="animate-float" style={{animationDelay: '0.4s'}}>
                    <div className="w-16 h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 2px 8px rgba(16, 185, 129, 0.15))'}}>
                      <img src="/msitral.png" alt="Mistral" className="w-10 h-10 object-contain" />
                    </div>
                  </div>
                </div>
                {/* DESKTOP: disposition large */}
                <>
                  {/* Top Left - Far (pixel effect) */}
                  <div className="absolute -top-20 -left-20 sm:-left-32 sm:-top-24 hidden sm:block animate-float" style={{animationDelay: '0s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center pixelate-effect" style={{filter: 'drop-shadow(0 2px 8px rgba(16, 185, 129, 0.15))'}}>
                      <img src="/gpt.png" alt="ChatGPT" className="w-7 h-7 sm:w-9 sm:h-9 object-contain pixelate-img" />
                      {/* Pixel overlay */}
                      <div className="pixel-overlay" />
                    </div>
                  </div>

                  {/* Top Center-Left (normal) */}
                  <div className="absolute -top-16 left-1/4 sm:left-1/3 transform -translate-x-1/2 hidden sm:block animate-float" style={{animationDelay: '0.1s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/claude.png" alt="Claude" className="w-7 h-7 sm:w-9 sm:h-9 object-contain" />
                    </div>
                  </div>

                  {/* Top Center-Right (pixel effect) */}
                  <div className="absolute -top-16 right-1/4 sm:right-1/3 transform translate-x-1/2 hidden sm:block animate-float" style={{animationDelay: '0.2s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center pixelate-effect" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/gemini.png" alt="Gemini" className="w-7 h-7 sm:w-9 sm:h-9 object-contain pixelate-img" />
                      <div className="pixel-overlay" />
                    </div>
                  </div>

                  {/* Top Right - Far (normal) */}
                  <div className="absolute -top-20 -right-20 sm:-right-32 sm:-top-24 hidden sm:block animate-float" style={{animationDelay: '0.3s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 2px 8px rgba(16, 185, 129, 0.15))'}}>
                      <img src="/msitral.png" alt="Mistral" className="w-7 h-7 sm:w-9 sm:h-9 object-contain" />
                    </div>
                  </div>

                  {/* Left - Middle (normal) */}
                  <div className="absolute top-1/2 -left-24 sm:-left-40 transform -translate-y-1/2 hidden sm:block animate-float" style={{animationDelay: '0.4s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/grok.png" alt="Grok" className="w-7 h-7 sm:w-9 sm:h-9 object-contain" />
                    </div>
                  </div>

                  {/* Right - Middle (pixel effect) */}
                  <div className="absolute top-1/2 -right-24 sm:-right-40 transform -translate-y-1/2 hidden sm:block animate-float" style={{animationDelay: '0.5s'}}>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 border-emerald-500/30 bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-sm flex items-center justify-center pixelate-effect" style={{filter: 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.2))'}}>
                      <img src="/deepseek.png" alt="DeepSeek" className="w-7 h-7 sm:w-9 sm:h-9 object-contain pixelate-img" />
                      <div className="pixel-overlay" />
                    </div>
                  </div>
                </>
              </div>

              <style>{`
                @keyframes float {
                  0%, 100% { transform: translateY(0px) translateZ(0px); opacity: 0.5; filter: drop-shadow(0 2px 8px rgba(16, 185, 129, 0.1)); }
                  50% { transform: translateY(-20px) translateZ(30px); opacity: 0.9; filter: drop-shadow(0 12px 24px rgba(16, 185, 129, 0.3)); }
                }
                .animate-float {
                  animation: float 4s ease-in-out infinite;
                }
                /* Pixelation effect for logos */
                .pixelate-effect {
                  position: relative;
                  overflow: visible;
                }
                .pixelate-img {
                  image-rendering: pixelated;
                  filter: contrast(1.2) saturate(1.2) brightness(1.1) drop-shadow(0 0 6px #00ffae60);
                  animation: pixel-glitch 2.2s steps(2, end) infinite alternate;
                }
                .pixel-overlay {
                  pointer-events: none;
                  position: absolute;
                  inset: 0;
                  z-index: 2;
                  background: repeating-linear-gradient(90deg, transparent, transparent 2px, #00ffae22 Z2px, #00ffae22 4px), repeating-linear-gradient(180deg, transparent, transparent 2px, #00ffae22 2px, #00ffae22 4px);
                  opacity: 0.18;
                  border-radius: 0.75rem;
                  mix-blend-mode: lighten;
                  animation: pixel-overlay-flicker 1.2s steps(2, end) infinite alternate;
                }
                @keyframes pixel-glitch {
                  0% { filter: contrast(1.2) saturate(1.2) brightness(1.1) drop-shadow(0 0 6px #00ffae60); }
                  10% { filter: contrast(1.5) saturate(1.5) brightness(1.2) drop-shadow(0 0 10px #00ffae90); }
                  20% { filter: contrast(1.1) saturate(1.1) brightness(1.0) drop-shadow(0 0 4px #00ffae40); }
                  30% { filter: contrast(1.3) saturate(1.3) brightness(1.3) drop-shadow(0 0 12px #00ffae80); }
                  100% { filter: contrast(1.2) saturate(1.2) brightness(1.1) drop-shadow(0 0 6px #00ffae60); }
                }
                @keyframes pixel-overlay-flicker {
                  0% { opacity: 0.18; }
                  50% { opacity: 0.32; }
                  100% { opacity: 0.18; }
                }
              `}</style>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                <span className="whitespace-nowrap">
                {t('heroTitle')}
                 </span>
              <br />
                <span className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 bg-clip-text text-transparent">
                {t('heroSubtitle')}
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto">
                {t('heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
                      navigator.vibrate(30);
                    }
                    navigate('/signup');
                  }}
                  aria-label={t('getStarted')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-500 text-slate-950 font-semibold text-lg hover:bg-emerald-400 transition shadow-xl shadow-emerald-500/20"
                >
                  {t('getStarted')}
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
                      navigator.vibrate(30);
                    }
                    navigate('/pricing-free');
                  }}
                  aria-label={t('viewPricing')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/10 bg-white/5 text-white font-semibold text-lg hover:bg-white/10 transition"
                >
                  {t('viewPricing')}
                </button>
              </div>
            </div>
          }
        >
          {/* YouTube Video Demo */}
          <YouTubeVideo 
            videoId="JCI-0qqWhM8"
            title={t('dashboardTitle')}
          />
        </ContainerScroll>
      </div>

      {/* Boost Section */}
    <div className="text-center text-white transition-transform transform translate-y-4 hover:translate-y-0 duration-500 ease-out" style={{marginTop: '-350px', paddingTop: 0}} >
        <h2 className="text-2xl font-bold">{t('boostTitle')}</h2>
        <p className="text-lg text-slate-400">{t('boostDescription')}</p>
      </div>

      {/* Features Section */}
      <section aria-label={t('featuresTitle')}>
        <FeaturesSectionWithHoverEffects />
      </section>

      {/* AI Models Showcase Section */}
      <section className="relative w-full py-12 px-6" aria-label="Supported AI Models">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              {t('aiModelsTitle')}
            </h3>
            <p className="text-lg text-slate-400">
              {t('aiModelsSubtitle')}
            </p>
          </div>

          {/* Models Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 items-center justify-center">
            {/* ChatGPT */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/gpt.png" alt="ChatGPT" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">ChatGPT</p>
            </div>

            {/* Claude */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/claude.png" alt="Claude" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">Claude</p>
            </div>

            {/* Gemini */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/gemini.png" alt="Gemini" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">Gemini</p>
            </div>

            {/* Mistral */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/msitral.png" alt="Mistral" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">Mistral</p>
            </div>

            {/* Grok */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/grok.png" alt="Grok" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">Grok</p>
            </div>

            {/* DeepSeek */}
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 hover:from-slate-900/80 transition-all duration-300 group cursor-pointer">
              <img src="/deepseek.png" alt="DeepSeek" className="w-16 h-16 md:w-20 md:h-20 mb-3 group-hover:scale-110 transition-transform duration-300 object-contain" />
              <p className="text-white font-semibold text-center text-sm md:text-base">DeepSeek</p>
            </div>
          </div>

          {/* Bottom tagline */}
          
        </div>
      </section>

            {/* Testimonials Section */}
      <section className="relative w-full py-20 px-6" aria-label={t('testimonialsTitle')}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {t('testimonialsTitle')}
            </h2>
            <p className="text-lg text-slate-400">
              {t('testimonialsSubtitle')}
            </p>
          </div>
          
          <div className="pt-10">
            <div className="relative mx-auto flex items-center justify-center">
              <Carousel
                opts={{ loop: true }}
                plugins={[AutoScroll({ playOnInit: true, speed: 3 })]}
              >
                <CarouselContent className="ml-0">
                  {[
                    {
                      name: t('testimonial1Name'),
                      role: t('testimonial1Role'),
                      rating: 5,
                      comment: t('testimonial1Comment')
                    },
                    {
                      name: t('testimonial2Name'),
                      role: t('testimonial2Role'),
                      rating: 5,
                      comment: t('testimonial2Comment')
                    },
                    {
                      name: t('testimonial3Name'),
                      role: t('testimonial3Role'),
                      rating: 5,
                      comment: t('testimonial3Comment')
                    },
                    {
                      name: t('testimonial4Name'),
                      role: t('testimonial4Role'),
                      rating: 5,
                      comment: t('testimonial4Comment')
                    },
                    {
                      name: t('testimonial5Name'),
                      role: t('testimonial5Role'),
                      rating: 5,
                      comment: t('testimonial5Comment')
                    },
                    {
                      name: t('testimonial6Name'),
                      role: t('testimonial6Role'),
                      rating: 5,
                      comment: t('testimonial6Comment')
                    }
                  ].map((testimonial, index) => (
                    <CarouselItem
                      key={index}
                      className="flex basis-full sm:basis-1/2 lg:basis-1/3 justify-center pl-0"
                    >
                      <div className="mx-6 sm:mx-10 flex shrink-0 items-center justify-center w-full">
                        <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-xl p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-300 w-full max-w-sm">
                          <div className="flex items-center gap-1 mb-3">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <svg key={i} className="w-5 h-5 fill-emerald-400" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <p className="text-slate-300 mb-4 italic text-sm sm:text-base">"{testimonial.comment}"</p>
                          <div className="border-t border-slate-700/50 pt-3">
                            <p className="text-white font-semibold text-sm sm:text-base">{testimonial.name}</p>
                            <p className="text-xs sm:text-sm text-emerald-400">{testimonial.role}</p>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </section>
      {/* Effet sparkles sous les avis */}

  <div style={{position: 'relative', left: 0, right: 0, width: '100vw', marginLeft: 'calc(-50vw + 50%)', height: '18rem', marginTop: '32px', marginBottom: '-200px'}}>
          <SparklesPreview />
        </div>

      {/* Multi-LLM Info - simple inline text */}
      <p className="max-w-2xl mx-auto mt-6 mb-6 text-center text-base md:text-lg text-slate-300">
        <span className="font-semibold text-emerald-400">Hiperyon</span> vous permet d'utiliser plusieurs modèles d'IA (ChatGPT, Gemini, Mistral, Grok...) en même temps, sans rester bloqué sur un seul. <br />
        Gagnez <span className="font-semibold text-emerald-400">3 à 4x</span> plus de temps et jusqu'à <span className="font-semibold text-emerald-400">200% d'efficacité</span> en choisissant le LLM qui vous convient à chaque instant.
      </p>

      {/* Section avec mockup */}
      <div style={{marginTop: '-180px'}}>
        <SectionWithMockup
          title={t('mockupTitle')}
          description={t('mockupDescription')}
          primaryImageSrc="/extion_first.png"
          secondaryImageSrc="/extion_vue.png"
        />
      </div>



  {/* FAQ Section */}
  <section className="relative w-full py-6 px-6" aria-label={t('faqTitle')}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {t('faqTitle')}
            </h2>
            <p className="text-lg text-slate-400">
              {t('faqSubtitle')}
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: t('faq1Q'),
                answer: t('faq1A')
              },
              {
                question: t('faq2Q'),
                answer: t('faq2A')
              },
              {
                question: t('faq3Q'),
                answer: t('faq3A')
              },
              {
                question: t('faq4Q'),
                answer: t('faq4A')
              },
              {
                question: t('faq5Q'),
                answer: t('faq5A')
              },
              {
                question: t('faq6Q'),
                answer: t('faq6A')
              },
              {
                question: t('faq7Q'),
                answer: t('faq7A')
              },
              {
                question: t('faq8Q'),
                answer: t('faq8A')
              },
              {
                question: t('faq9Q'),
                answer: t('faq9A')
              },
              {
                question: t('faq10Q'),
                answer: t('faq10A')
              },
              {
                question: t('faq11Q'),
                answer: t('faq11A')
              },
              {
                question: t('faq12Q'),
                answer: t('faq12A')
              }
            ].map((faq, index) => (
              <details 
                key={index}
                className="group bg-slate-900/30 backdrop-blur-sm border border-slate-800/50 rounded-xl overflow-hidden hover:border-emerald-500/30 transition-all duration-300"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  <span className="text-emerald-400 text-2xl group-open:rotate-45 transition-transform duration-300">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-4 text-slate-300 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section - Harmonized Background */}
      <section className="relative w-full py-20 px-6 bg-gradient-to-br from-slate-950 via-black to-slate-950" aria-label={t('newsletterTitle')}>
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-950 via-black to-slate-950 rounded-3xl p-12 border border-slate-700/50 shadow-xl backdrop-blur-lg">
            {/* Decorative elements */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl" aria-hidden="true"></div>
            
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
                {t('newsletterTitle')}
              </h2>
              <p className="text-lg md:text-xl text-slate-300 mb-8">
                {t('newsletterDescription')}
              </p>
              
                <form
                  className="flex flex-col sm:flex-row gap-4 items-center justify-center max-w-2xl mx-auto"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.target;
                    const email = form.email.value.trim();
                    if (!email) return;
                    const lang = navigator.language ? navigator.language.split('-')[0] : 'fr';
                    try {
                      const res = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/tools/newsletter/add', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, lang })
                      });
                      const data = await res.json();
                      if (data.message) {
                        setNewsletterMsg(`✅ ${data.message}`);
                      } else if (data.detail) {
                        setNewsletterMsg(`⚠️ ${data.detail}`);
                      } else {
                        setNewsletterMsg('❌ Unexpected error');
                      }
                    } catch (err) {
                      setNewsletterMsg('❌ Network error');
                    }
                  }}
                >
                  <input
                    type="email"
                    name="email"
                    placeholder={t('newsletterPlaceholder')}
                    required
                    className="w-full sm:flex-1 px-6 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-slate-400 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  />
                  <button
                    type="submit"
                    onClick={() => {
                      if (typeof window !== 'undefined' && 'vibrate' in navigator) {
                        navigator.vibrate(30);
                      }
                    }}
                    className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-bold hover:from-emerald-400 hover:to-emerald-500 transition shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 whitespace-nowrap"
                  >
                    {t('newsletterButton')}
                  </button>
                </form>
                {newsletterMsg && (
                  <p className="text-sm text-emerald-400 mt-4">{newsletterMsg}</p>
                )}
                <p className="text-sm text-slate-400 mt-4">
                  {t('newsletterDisclaimer')}
                </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer>
        {/* ...existing footer content... */}
        <div className="mt-4 text-center">
          <Link to="/mentions-legales" className="text-emerald-400 hover:underline text-sm">Mentions légales</Link>
        </div>
      </Footer>

      {/* Cookie Consent Banner */}
      <GlobalCookieConsent />
      
      {/* Product Hunt Widget */}
      <ProductHuntWidget />
    </div>
  )
}